import React, { useState, useMemo } from 'react';
import { utils, writeFile } from 'xlsx';
import useUserActivities from '../hooks/useUserActivities';

const UserActivity = () => {
    const { activities, loading, error, reloadData } = useUserActivities();
    const [filters, setFilters] = useState({
        userId: '',
        activityDate: '',
        blockStatus: 'all',
    });
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending',
    });

    // Применение фильтров
    const filteredActivities = useMemo(() => {
        try {
            if (!activities || !Array.isArray(activities)) return [];
    
            return activities.filter((activity) => {
                if (!activity) return false;
    
                // Безопасное получение значений
                const userId = String(activity.userId || '').toLowerCase();
                const searchUserId = String(filters.userId || '').toLowerCase();
                
                const activityDate = activity.lastActivity 
                    ? new Date(activity.lastActivity).toISOString().split('T')[0] 
                    : '';
                
                const blockStatus = activity.blockTime !== null && activity.blockTime !== undefined 
                    ? 'blocked' 
                    : 'unblocked';
    
                // Применение фильтров
                const matchesUserId = searchUserId === '' || userId.includes(searchUserId);
                const matchesActivityDate = filters.activityDate === '' || activityDate === filters.activityDate;
                const matchesBlockStatus = filters.blockStatus === 'all' || filters.blockStatus === blockStatus;
    
                return matchesUserId && matchesActivityDate && matchesBlockStatus;
            });
        } catch (error) {
            console.error('Filtering error:', error);
            return activities || []; // Возвращаем исходные данные в случае ошибки
        }
    }, [activities, filters]);

    // Сортировка данных
    const sortedData = useMemo(() => {
        if (!filteredActivities || !Array.isArray(filteredActivities)) return [];

        const sortableData = [...filteredActivities];
        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                if (!a || !b) return 0;

                // Обработка отсутствующих значений
                if (!a[sortConfig.key] && !b[sortConfig.key]) return 0;
                if (!a[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
                if (!b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;

                let comparison = 0;
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    comparison = aValue.localeCompare(bValue);
                } else if (sortConfig.key === 'lastActivity' || sortConfig.key === 'blockTime') {
                    comparison = new Date(aValue).getTime() - new Date(bValue).getTime();
                } else {
                    comparison = aValue > bValue ? 1 : -1;
                }

                return sortConfig.direction === 'ascending' ? comparison : -comparison;
            });
        }
        return sortableData;
    }, [filteredActivities, sortConfig]);

    // Обработчик сортировки
    const requestSort = (key) => {
        if (sortConfig.key === key) {
            setSortConfig({
                key,
                direction: sortConfig.direction === 'ascending' ? 'descending' : 'ascending',
            });
        } else {
            setSortConfig({
                key,
                direction: 'ascending',
            });
        }
    };

    // Сброс фильтров
    const resetFilters = () => {
        setFilters({
            userId: '',
            activityDate: '',
            blockStatus: 'all',
        });
    };

    // Экспорт в Excel
    const exportToExcel = () => {
        try {
            const dataToExport = sortedData.map((activity) => ({
                'User ID': activity?.userId || '',
                'Последняя активность': activity?.lastActivity
                    ? new Date(activity.lastActivity).toLocaleString()
                    : 'Нет данных',
                'Время блокировки': activity?.blockTime
                    ? new Date(activity.blockTime).toLocaleString()
                    : '---',
            }));

            const worksheet = utils.json_to_sheet(dataToExport);
            const workbook = utils.book_new();
            utils.book_append_sheet(workbook, worksheet, 'Активность пользователей');
            writeFile(workbook, 'Активность_пользователей.xlsx');
        } catch (err) {
            console.error('Export error:', err);
        }
    };

    return (
        <div className="user-activities-container">
            <h2>Список блокировок</h2>

            <div className="controls">
                <div className="filter-group">
                    <label htmlFor="userIdFilter">
                        User ID:
                        <input
                            id="userIdFilter"
                            type="text"
                            value={filters.userId}
                            onChange={(e) =>
                                setFilters({ ...filters, userId: e.target.value })
                            }
                            placeholder="Фильтр по User ID"
                        />
                    </label>

                    <label htmlFor="activityDateFilter">
                        Дата активности:
                        <input
                            id="activityDateFilter"
                            type="date"
                            value={filters.activityDate}
                            onChange={(e) =>
                                setFilters({ ...filters, activityDate: e.target.value })
                            }
                            max={new Date().toISOString().split('T')[0]}
                        />
                    </label>
                </div>

                <div className="action-buttons">
                    <button onClick={resetFilters} className="reset-btn">
                        Сбросить фильтры
                    </button>
                    <button
                        onClick={exportToExcel}
                        className="export-btn"
                        disabled={!sortedData || sortedData.length === 0 || loading}
                    >
                        {loading ? 'Загрузка...' : 'Экспорт в Excel'}
                    </button>
                </div>
            </div>
            
            {loading ? (
                <div className="loading">
                    <p>Загрузка данных...</p>
                </div>
            ) : (
                <div className="table-container">
                    <table>
                        <thead>
                            <tr>
                                <th onClick={() => requestSort('id')}>
                                    № {sortConfig.key === 'id' && (
                                        sortConfig.direction === 'ascending' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => requestSort('userId')}>
                                    User ID {sortConfig.key === 'userId' && (
                                        sortConfig.direction === 'ascending' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => requestSort('lastActivity')}>
                                    Дата регистрации {sortConfig.key === 'lastActivity' && (
                                        sortConfig.direction === 'ascending' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => requestSort('blockTime')}>
                                    Дата блокировки {sortConfig.key === 'blockTime' && (
                                        sortConfig.direction === 'ascending' ? '↑' : '↓'
                                    )}
                                </th>
                                <th onClick={() => requestSort('restartTime')}>
                                    Дата рестарта{sortConfig.key === 'restartTime' && (
                                        sortConfig.direction === 'ascending' ? '↑' : '↓'
                                    )}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.length > 0 ? (
                                sortedData.map(activity => (
                                    activity ? (
                                        <tr key={activity.id}>
                                            <td>{activity.id}</td>
                                            <td>{activity.userId}</td>
                                            <td>{activity.lastActivity ? new Date(activity.lastActivity).toLocaleString() : 'Нет данных'}</td>
                                            <td>{activity.blockTime ? new Date(activity.blockTime).toLocaleString() : '---'}</td>
                                            <td>{activity.restartTime ? new Date(activity.restartTime).toLocaleString() : '---'}</td>
                                        </tr>
                                    ) : null
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="no-data">
                                        Нет данных для отображения
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <style>{`
                .user-activities-container {
                    padding: 20px;
                    font-family: Arial, sans-serif;
                    max-width: 1200px;
                    margin: 0 auto;
                }
                
                h2 {
                    margin-bottom: 20px;
                    color: #333;
                }
                
                .controls {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                    gap: 15px;
                    background: #f8f9fa;
                    padding: 15px;
                    border-radius: 8px;
                }
                
                .filter-group {
                    display: flex;
                    gap: 15px;
                    flex-wrap: wrap;
                    align-items: flex-end;
                }
                
                .filter-group label {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    font-size: 14px;
                }
                
                .filter-group input, .filter-group select {
                    padding: 8px 12px;
                    border: 1px solid #ced4da;
                    border-radius: 4px;
                    font-size: 14px;
                }
                
                .action-buttons {
                    display: flex;
                    gap: 10px;
                    align-items: flex-end;
                }
                
                button {
                    padding: 8px 16px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 14px;
                }
                
                button:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                
                .reset-btn {
                    background-color: #6c757d;
                    color: white;
                }
                
                .export-btn {
                    background-color: #28a745;
                    color: white;
                }
                
                button:hover:not(:disabled) {
                    opacity: 0.9;
                    transform: translateY(-1px);
                }
                
                .table-container {
                    overflow-x: auto;
                    border-radius: 8px;
                    box-shadow: 0 0 10px rgba(0,0,0,0.05);
                }
                
                table {
                    width: 100%;
                    border-collapse: collapse;
                    margin-top: 20px;
                    background: white;
                }
                
                th, td {
                    padding: 12px 15px;
                    text-align: left;
                    border-bottom: 1px solid #dee2e6;
                }
                
                th {
                    background-color: #f8f9fa;
                    cursor: pointer;
                    position: relative;
                    font-weight: 600;
                    color: #495057;
                }
                
                th:hover {
                    background-color: #e9ecef;
                }
                
                tr:hover {
                    background-color: #f8f9fa;
                }
                
                .no-data {
                    text-align: center;
                    color: #6c757d;
                    padding: 20px;
                }
                
                .loading, .error-message {
                    padding: 20px;
                    text-align: center;
                    background: #f8f9fa;
                    border-radius: 8px;
                    margin: 20px 0;
                }
                
                .error-message {
                    color: #dc3545;
                    background: #fff3f3;
                    margin-bottom: 20px;
                }
                
                @media (max-width: 768px) {
                    .controls {
                        flex-direction: column;
                    }
                    
                    .action-buttons {
                        justify-content: flex-end;
                    }
                }
            `}</style>
        </div>
    );
};


export default UserActivity;